// Browser / OS fingerprinting for session device display.
// Pure functions — no React, no DOM. Used by AccountPage's SessionsSection.

/** Parse a raw User-Agent string into browser name, version, and OS. */
export function parseUserAgent(ua: string): { browser: string; os: string; version?: string } {
  let browser = "Unknown";
  let version: string | undefined;

  // Chrome (must come before Safari since Chrome UA contains "Chrome")
  const chromeMatch = ua.match(/Chrome\/(\d+)/);
  if (chromeMatch && !ua.includes("Edg/") && !ua.includes("OPR/") && !ua.includes("Brave/")) {
    browser = "Chrome";
    version = chromeMatch[1];
  }
  // Edge
  const edgeMatch = ua.match(/Edg\/(\d+)/);
  if (edgeMatch) {
    browser = "Edge";
    version = edgeMatch[1];
  }
  // Firefox
  const firefoxMatch = ua.match(/Firefox\/(\d+)/);
  if (firefoxMatch) {
    browser = "Firefox";
    version = firefoxMatch[1];
  }
  // Safari (not Chrome, not Edge, not Brave)
  const safariMatch = ua.match(/Version\/(\d+)(?:\.(\d+))?/);
  if (safariMatch && ua.includes("Safari/") && !chromeMatch && !edgeMatch && !ua.includes("Brave/")) {
    browser = "Safari";
    version = safariMatch[2] ? `${safariMatch[1]}.${safariMatch[2]}` : safariMatch[1];
  }
  // Opera
  const operaMatch = ua.match(/OPR\/(\d+)/);
  if (operaMatch) {
    browser = "Opera";
    version = operaMatch[1];
  }
  // Brave
  if (ua.includes("Brave/")) {
    browser = "Brave";
    version = chromeMatch?.[1];
  }

  let os = "Unknown";
  if (/Mac OS X [\d_]+/i.test(ua)) {
    os = ua.match(/Mac OS X ([\d_]+)/i)?.[0].replace(/_/g, ".") ?? "macOS";
  } else if (/Windows NT/i.test(ua)) {
    const nt = ua.match(/Windows NT (\d+\.\d+)/i);
    if (nt) {
      const v = nt[1];
      os = v === "10.0" ? "Windows 10/11" : `Windows ${v}`;
    } else {
      os = "Windows";
    }
  } else if (/Android\s([\d.]+)/i.test(ua)) {
    os = `Android ${ua.match(/Android\s([\d.]+)/i)?.[1]}`;
  } else if (/iPhone OS ([\d_]+)/i.test(ua)) {
    os = ua.match(/iPhone OS ([\d_]+)/i)?.[0].replace(/_/g, ".") ?? "iOS";
  } else if (/iPad; CPU OS/i.test(ua)) {
    os = `iPadOS ${ua.match(/OS ([\d_]+)/i)?.[1]?.replace(/_/g, ".") ?? ""}`;
  } else if (/Linux/i.test(ua)) {
    os = "Linux";
  }

  return { browser, os, version };
}

/** Format a user-agent into a human-friendly label. Returns the raw string if it already
 * looks pre-formatted (no slashes, contains " on "), otherwise parses it. */
export function formatDevice(ua: string | null): string {
  if (!ua) return "Unknown device";
  if (!ua.includes("/") && /\s+on\s+/.test(ua)) return ua;

  const { browser, os, version } = parseUserAgent(ua);
  const parts = [browser, version, os].filter((p) => p && p !== "Unknown");
  return parts.length > 0 ? parts.join(" · ") : "Unknown device";
}
